import { isPublicErrorCode, normalizePublicErrorInput } from "@nexra/contracts";
import { createPortal } from "react-dom";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { StatusBadge, statusLabel } from "./components/ui/status-badge";
export { StatusBadge, statusLabel } from "./components/ui/status-badge";
import { catalogQueryPolicy, sensitiveQueryPolicy } from "./query-policy";
import { useAccountQueryKey } from "./account-query";
import type { JsonObject, JsonValue, ModelSchema, Task, TaskModelSummary } from "@nexra/contracts";
import { modelInputDefaults, orderedSchemaProperties } from "@nexra/contracts";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
export { DataTable } from "./components/ui/data-table";

import { ApiError, dataApi } from "./api";
import { videoPosterUrl } from "./lib/video-poster";
import { getLocale, hasTranslation, translateKey } from "./i18n";
import { localizedParameterDescription } from "./i18n/parameter-descriptions";
import { useTranslation } from "react-i18next";
import { AdminSelectMenu } from "./components/admin/admin-select-menu";
import { SelectField } from "./components/select-field";
import { ContentPartsField } from "./content-parts-field";
import { ModelIcon } from "./model-icon";
import { Segmented } from "./components/ui/segmented";
import { Switch } from "./components/ui/switch";
import { TermExplanation } from "./components/ui/term-explanation";
import { Info } from "lucide-react";
import { Spinner } from "./components/ui/spinner";
import { PublicErrorSummary } from "./components/public-error-summary";
export { EmptyState, ErrorState } from "./components/ui/list-state";
import { EmptyState } from "./components/ui/list-state";

export function PageHeader({
  title,
  description,
  actions,
  className = "",
}: Readonly<{ title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }>) {
  return (
    <header className={`page-header${className ? ` ${className}` : ""}`}>
      <div className="min-w-0">
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function LoadingBlock({ label, className = "" }: Readonly<{ label?: string; className?: string }>) {
  useTranslation();
  const text = label ?? translateKey("common.loading");
  return (
    <div className={`loading-block${className ? ` ${className}` : ""}`} role="status">
      <Spinner aria-hidden="true" role={undefined} aria-label={undefined} />
      <span>{text}</span>
    </div>
  );
}

import { formatModelSlug } from "./model-family";

export function ModelCard({
  model,
}: Readonly<{
  model: TaskModelSummary;
  versions?: TaskModelSummary[];
}>) {
  useTranslation();
  const thumbnail = safeUrl(model.thumbnailUrl);
  const tags = model.tags.filter((tag) => !/^(legacy|provider|source|type):?/i.test(tag));
  const [provider] = model.slug.split("/");
  const cleanId = formatModelSlug(model.slug);
  const [copied, setCopied] = useState(false);
  const isRecommended = Boolean(model.isRecommended || model.featured);

  return (
    <article className="group min-w-0 overflow-hidden rounded-sm border border-zinc-200 bg-white transition hover:border-zinc-300 hover:shadow-[0_10px_30px_rgba(24,24,27,0.07)]">
      <Link
        className={`model-thumbnail model-card-visual modality-${model.modality}`}
        to="/models/$"
        params={{ _splat: model.slug }}
        aria-label={`打开 ${cleanId}`}
      >
        {thumbnail ? (
          <img src={thumbnail} alt={model.displayName} loading="lazy" />
        ) : (
          <div className="model-card-fallback">
            <span>{operationLabel(model.modality)}</span>
            <strong>{operationLabel(model.operation)}</strong>
          </div>
        )}
        {isRecommended ? <Badge className="featured-badge">{translateKey("workspace.recommended")}</Badge> : null}
      </Link>
      <div className="p-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <ModelIcon size="md" source={[model.slug, provider, model.displayName, model.family]} />
          <div className="min-w-0 flex-1">
            <Link className="block min-w-0 truncate" to="/models/$" params={{ _splat: model.slug }}>
              <strong className="block truncate text-[13px] font-semibold leading-5 text-zinc-950" title={cleanId}>
                {cleanId}
              </strong>
            </Link>
            <span className="block truncate text-xs text-zinc-500">{model.displayName}</span>
          </div>
          <button
            className="flex size-8 shrink-0 items-center justify-center rounded-md border border-transparent text-xs text-zinc-400 opacity-0 transition hover:border-zinc-200 hover:bg-zinc-50 hover:text-zinc-950 group-hover:opacity-100 focus:opacity-100"
            onClick={async () => {
              await navigator.clipboard.writeText(cleanId);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1_500);
            }}
            title="复制模型 ID"
            type="button"
          >
            {copied ? "✓" : <CopyIcon />}
          </button>
        </div>

        <p className="mt-3 line-clamp-2 min-h-10 text-xs leading-5 text-zinc-600">
          {localizedCatalogText(model.summary, `${model.displayName}，支持${operationLabel(model.operation)}。`)}
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(model.capabilities.inputModes.length > 0
            ? model.capabilities.inputModes.slice(0, 2)
            : [model.operation]
          ).map((capability) => (
            <Badge className="bg-zinc-100 text-zinc-600" key={capability}>
              {operationLabel(capability)}
            </Badge>
          ))}
          {model.endpoints.slice(0, 1).map((endpoint) => (
            <Badge className="bg-zinc-100 text-zinc-800" key={endpoint.key}>
              {endpoint.label}
            </Badge>
          ))}
          {tags.slice(0, 1).map((tag) => (
            <Badge className="bg-zinc-100 text-zinc-600" key={tag}>
              {tag}
            </Badge>
          ))}
        </div>
      </div>
    </article>
  );
}

function CopyIcon() {
  return <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 24 24" width="14"><rect height="13" rx="2" stroke="currentColor" strokeWidth="1.6" width="13" x="8" y="8"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="1.6"/></svg>;
}

export function TaskPreview({ task, compact = false, showMeta = true, showPreview = true }: Readonly<{ task: Task<JsonValue>; compact?: boolean; showMeta?: boolean; showPreview?: boolean }>) {
  useTranslation();
  const asset = task.assets[0];
  const url = safeMediaUrl(asset?.url || asset?.downloadUrl, asset?.kind);
  const prompt = taskInputSummary(task.input);
  return (
    <article className={`task-preview ${compact ? "compact" : ""}`}>
      {showPreview ? <div className="task-media">
        {url && asset ? <TaskMediaPreview key={`${asset.id}:${url}`} kind={asset.kind} url={url} poster={videoPosterUrl({ assets: task.assets, output: task.output })} label={`${task.model} 输出`} /> : null}
        {!url ? (
          <div className="task-placeholder">
            <StatusBadge value={task.status} />
            <span role={task.error ? "alert" : undefined}>{normalizePublicErrorInput(task)?.nexraError.message || task.progress?.message || operationLabel(task.operation)}</span>
          </div>
        ) : null}
      </div> : null}
      {showPreview && task.assets.length > 1 ? <Link className="block px-3 py-2 text-xs text-zinc-800" to="/models/$" params={{ _splat: task.model }} search={{ tab: "playground", taskId: task.id }}>{translateKey("workspace.viewOutputs", { count: task.assets.length })}</Link> : null}
      {showMeta ? <div className="task-preview-meta">
        <div><strong>{task.model}</strong><StatusBadge value={task.status} /></div>
        {prompt ? <p className="line-clamp-2 text-xs leading-5 text-zinc-600" title={prompt}>{prompt}</p> : null}
        <span>{formatDate(task.createdAt)}{task.cost ? ` · ${formatListedPrice(task.cost.currency, task.cost.amount)}` : ""}</span>
      </div> : null}
    </article>
  );
}

export function taskInputSummary(input?: JsonObject): string {
  if (!input) return "";
  for (const key of ["prompt", "texts", "text", "content", "input", "messages", "text_prompt", "instructions", "description"]) {
    const value = input[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (Array.isArray(value)) {
      const text = value.flatMap((item) => {
        if (typeof item === "string") return [item];
        if (!item || typeof item !== "object" || Array.isArray(item)) return [];
        const content = item.type === "text" ? item.text : item.content;
        return typeof content === "string" ? [content] : [];
      }).join(" ").trim();
      if (text) return text;
    }
  }
  return "";
}

export function TaskResult({ task, mode = "preview" }: Readonly<{ task?: Task<JsonValue>; mode?: "preview" | "json" }>) {
  useTranslation();
  if (!task) {
    return <EmptyState title={translateKey("workspace.waiting")} detail="提交后，生成状态和媒体结果会显示在这里。" />;
  }
  const error = normalizePublicErrorInput(task);
  const media = task.assets.flatMap((asset) => {
    const url = safeMediaUrl(asset.url || asset.downloadUrl, asset.kind);
    return url ? [{ asset, url }] : [];
  });
  return (
    <div className="result-panel">
      <div className="result-content">
        {mode === "json" ? <pre className="code-block m-0 h-full max-h-none w-full self-stretch rounded-none">{JSON.stringify(task, null, 2)}</pre> : null}
        {mode === "preview" && error ? <div className="error-state" role="alert">{isPublicErrorCode(error.nexraError.code)
          ? <PublicErrorSummary error={error} />
          : <><strong>{error.nexraError.code}</strong><p>{error.nexraError.message}</p></>}</div> : null}
        {mode === "preview" && media.length > 0 ? <div className="grid w-full gap-4 p-4">{media.map(({ asset, url }, index) => <figure className="m-0 min-w-0" key={asset.id}>
          <TaskMediaPreview key={`${asset.id}:${url}`} kind={asset.kind} url={url} poster={videoPosterUrl({ assets: task.assets, output: task.output })} label={`模型输出 ${index + 1}`} />
          <figcaption className="mt-2 text-xs text-zinc-500"><a href={safeUrl(asset.downloadUrl) ?? url} target="_blank" rel="noreferrer">{asset.name ?? `输出 ${index + 1}`} ↗</a></figcaption>
        </figure>)}</div> : null}
        {mode === "preview" && media.length === 0 && task.output ? task.operation === "audio.transcribe" && isJsonObject(task.output) && isJsonObject(task.output.result) && typeof task.output.result.text === "string"
          ? <pre aria-label={translateKey("asr.transcript")} className="m-0 whitespace-pre-wrap p-4 font-sans text-sm">{task.output.result.text}</pre>
          : <pre className="code-block">{JSON.stringify(task.output, null, 2)}</pre> : null}
        {mode === "preview" && media.length === 0 && !task.output && !error ? (
          <div className="task-running">
            <span className={task.status === "failed" ? "error-dot" : "pulse-dot"} />
            <strong>{statusLabel(task.progress?.stage || task.status)}</strong>
            <p>{task.progress?.message || (task.status === "cancelled" ? "任务已取消" : `进度 ${task.progress?.percent ?? 0}%`)}</p>
          </div>
        ) : null}
      </div>
      <div className="result-footer">
        <code title={task.id}>{shortId(task.id)}</code>
        {task.cost ? <span>{translateKey("workspace.cost")}{formatListedPrice(task.cost.currency, task.cost.amount)}</span> : null}
        {task.usage ? <span>{task.usage.components.map((item) => `${item.quantity} ${item.unit}`).join(" · ")}</span> : null}
      </div>
    </div>
  );
}

export function TaskMediaPreview({ kind, url, poster, label }: Readonly<{ kind: string; url: string; poster?: string; label: string }>) {
  useTranslation();
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
    <strong>{label}预览加载失败</strong>
    <p className="mt-1">资源无法访问或浏览器不支持此格式。可尝试重新加载或打开原始文件。</p>
    <Button variant="outline" className="mt-3" onClick={() => setFailed(false)} type="button">重新加载预览</Button>
    <a className="ml-3 text-xs underline" href={url} target="_blank" rel="noreferrer">{translateKey("workspace.openOriginal")}</a>
  </div>;
  const onError = () => setFailed(true);
  if (kind === "image") return <img src={url} alt={label} loading="lazy" onError={onError} />;
  if (kind === "video") return <video src={url} poster={poster} aria-label={label} controls preload="metadata" onError={onError} />;
  if (kind === "audio") return <audio src={url} aria-label={label} controls preload="metadata" onError={onError} />;
  return null;
}



export function SchemaInputEditor({
  schema,
  value,
  onChange,
  model,
  onValidityChange,
  hiddenKeys,
  expandAll = false,
  emptyNumberBehavior = "zero",
  formOnly = false,
  renderField: renderFieldOverride,
  textareaClassName,
  primaryContainer,
  referenceContainer,
  textActionsContainer,
  jsonContainer,
  fieldContainers,
  jsonPlacement = "primary",
  fieldPresentation,
}: Readonly<{
  schema?: ModelSchema;
  value: JsonObject;
  onChange(value: JsonObject): void;
  model?: string;
  onValidityChange?(valid: boolean): void;
  hiddenKeys?: readonly string[];
  expandAll?: boolean;
  emptyNumberBehavior?: "zero" | "omit";
  formOnly?: boolean;
  renderField?(key: string, editor: ReactNode): ReactNode;
  textareaClassName?: string;
  /** Render the same primary controls in a separate region, retaining field state and validation. */
  primaryContainer?: HTMLElement | null;
  /** Relocate existing controls without duplicating their state or validation. */
  referenceContainer?: HTMLElement | null;
  textActionsContainer?: HTMLElement | null;
  /** Keep JSON outside the composer while sharing the same input and validation. */
  jsonContainer?: HTMLElement | null;
  fieldContainers?: Readonly<Record<string, HTMLElement | null>>;
  jsonPlacement?: "primary" | "inline";
  fieldPresentation?: Readonly<Record<string, { title?: string; collapsible?: boolean; multiline?: boolean; appearance?: "composer"; placeholder?: string }>>;
}>) {
  const { i18n } = useTranslation();
  const [mode, setMode] = useState<"form" | "json">("form");
  const [showMore, setShowMore] = useState(false);
  const [draft, setDraft] = useState(() => JSON.stringify(value, null, 2));
  const [draftError, setDraftError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>({});
  const fields = useMemo(() => schemaFields(schema).filter((field) => {
    const ui = field.schema["x-ui"];
    return !hiddenKeys?.includes(field.key) && !(isJsonObject(ui) && ui.hidden === true);
  }), [hiddenKeys, i18n.language, schema]);

  useEffect(() => {
    setDraft(JSON.stringify(value, null, 2));
    setDraftError(null);
  }, [value]);
  useEffect(() => {
    onValidityChange?.(!draftError && !Object.values(invalidFields).some(Boolean));
  }, [draftError, invalidFields, onValidityChange]);

  const collapsedFields = useMemo(() => {
    const selected = fields.filter((field) => field.required || field.primary);
    const selectedKeys = new Set(selected.map((field) => field.key));
    // Optional source media is still a primary part of generation/editing.
    // Keep it visible before filling the compact form with defaulted controls.
    for (const field of fields) {
      if (selected.length >= 4) break;
      if (!selectedKeys.has(field.key) && isMediaField(field)) {
        selected.push(field);
        selectedKeys.add(field.key);
      }
    }
    for (const field of fields) {
      if (selected.length >= 4) break;
      if (!selectedKeys.has(field.key) && field.requiresReferenceName) {
        selected.push(field);
        selectedKeys.add(field.key);
      }
    }
    for (const field of fields) {
      if (selected.length >= 4) break;
      if (!selectedKeys.has(field.key) && hasEditorValue(schema?.defaults[field.key] ?? field.schema.default)) {
        selected.push(field);
        selectedKeys.add(field.key);
      }
    }
    return selected;
  }, [fields, schema?.defaults]);
  const visibleFields = expandAll || showMore ? fields : collapsedFields;
  const hasHiddenFields = visibleFields.length < fields.length;
  const canUseForm = fields.length > 0;

  function updateJson(next: string) {
    setDraft(next);
    try {
      const parsed = JSON.parse(next) as unknown;
      if (!isJsonObject(parsed)) throw new Error("输入必须是 JSON 对象");
      setDraftError(null);
      onChange(parsed);
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : "JSON 格式不正确");
    }
  }

  const isPrimaryInput = (field: SchemaFieldDefinition) =>
    !/^(system|system_prompt|systemPrompt|messages|history|conversation_history)$/.test(field.key)
    && (field.primary || /^(prompt|text|script)$/.test(field.key) || field.control === "content-parts" || isMediaField(field));
  const primaryFields = primaryContainer !== undefined ? visibleFields.filter(field => isPrimaryInput(field) && (!fieldContainers || !(field.key in fieldContainers))) : [];
  const relocatedFields = visibleFields.filter(field => fieldContainers && field.key in fieldContainers);
  const sideFields = (primaryContainer !== undefined ? visibleFields.filter(field => !isPrimaryInput(field)) : visibleFields)
    .filter(field => !fieldContainers || !(field.key in fieldContainers));
  function renderField(field: SchemaFieldDefinition) {
    const presentation = fieldPresentation?.[field.key];
    const editor = <SchemaField
              field={presentation?.title ? { ...field, title: presentation.title } : field}
              multiline={presentation?.multiline}
              appearance={presentation?.appearance}
              placeholder={presentation?.placeholder}
              referenceContainer={referenceContainer}
              textActionsContainer={textActionsContainer}
              key={field.key}
              model={model}
              emptyNumberBehavior={emptyNumberBehavior}
              textareaClassName={textareaClassName}
              value={value[field.key]}
              onValidityChange={(valid) => setInvalidFields((current) => Boolean(current[field.key]) === !valid ? current : { ...current, [field.key]: !valid })}
              onChange={(next) => {
                const updated = { ...value };
                if (next === undefined) delete updated[field.key];
                else updated[field.key] = next;
                if (!field.required && Array.isArray(next) && next.length === 0 && Number(field.schema.minItems) > 0) delete updated[field.key];
                onChange(updated);
              }}
            />;
    return <Fragment key={field.key}>{renderFieldOverride ? renderFieldOverride(field.key, editor) : editor}</Fragment>;
  }
  const jsonEditor = <label className="field">
          <span>{jsonContainer !== undefined ? "JSON" : translateKey("workspace.inputJson")}</span>
          <Textarea aria-label={jsonContainer !== undefined ? "JSON" : undefined} className="json-editor" value={draft} onChange={(event) => updateJson(event.target.value)} spellCheck={false} />
          {draftError ? <small className="field-error">{draftError}</small> : null}
        </label>;
  const detachedInput = jsonContainer !== undefined || (jsonPlacement === "inline" || mode === "form") && canUseForm ? <div className="field-stack">{primaryFields.map(renderField)}</div> : jsonEditor;
  return (
    <div className="schema-editor">
      {!formOnly && jsonContainer === undefined ? <Segmented<"form" | "json"> className="mb-4" ariaLabel={translateKey("studio.inputView")} value={mode} onChange={setMode} options={[{ value: "form", label: translateKey("studio.form"), disabled: !canUseForm }, { value: "json", label: "JSON" }]} /> : null}
      {mode === "form" && canUseForm ? (
        <div className="field-stack">
          {sideFields.map(field => fieldPresentation?.[field.key]?.collapsible ? <details key={field.key} className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">{fieldPresentation[field.key]?.title ?? field.title}</summary>
            <div className="mt-3">{renderField(field)}</div>
          </details> : renderField(field))}
          {hasHiddenFields ? (
            <button className="more-button" onClick={() => setShowMore((current) => !current)} type="button">
              {translateKey("workspace.moreSettings")}<span>{showMore ? "−" : "+"}</span>
            </button>
          ) : null}
        </div>
      ) : jsonContainer !== undefined ? null : (
        primaryContainer === undefined || jsonPlacement === "inline" ? jsonEditor : <p className="text-xs text-muted-foreground">{translateKey("sandbox.jsonInComposer")}</p>
      )}
      {jsonContainer ? createPortal(jsonEditor, jsonContainer) : null}
      {primaryContainer ? createPortal(detachedInput, primaryContainer) : null}
      {relocatedFields.map(field => fieldContainers?.[field.key] ? createPortal(renderField(field), fieldContainers[field.key]!, field.key) : null)}
    </div>
  );
}

type SchemaFieldDefinition = Readonly<{
  schema: JsonObject;
  key: string;
  title: string;
  description?: string;
  type: string;
  required: boolean;
  primary: boolean;
  control?: string;
  resource?: string;
  resourceModel?: string;
  options: JsonValue[];
  optionLabels: Readonly<Record<string, string>>;
  minimum?: number;
  maximum?: number;
  step?: number;
  unit?: string;
  autoValue?: JsonValue;
  roles: string[];
  accept: string[];
  maxItems?: number;
  requiresReferenceName: boolean;
}>;

/** Field defaults stay visible so a changed value reads as a deliberate deviation. */
function fieldDefaultHint(field: SchemaFieldDefinition): string {
  const fallback = field.schema.default;
  if (fallback === undefined || fallback === null || typeof fallback === "object") return "";
  if (typeof fallback === "boolean") return fallback ? translateKey("common.on") : translateKey("common.off");
  const text = String(fallback);
  return text.length > 24 ? "" : `${text}${field.unit ? ` ${field.unit}` : ""}`;
}

/** Studio parameters are chosen from fixed ladders instead of free-typed values. */
const NUMERIC_FIELD_PRESETS: Readonly<Record<string, readonly number[]>> = {
  seed: [42, 1024, 2024, 123456],
  output_compression: [100, 95, 90, 80, 70, 60, 50],
  chaos: [0, 10, 25, 50, 75, 100],
  stylize: [0, 100, 250, 500, 750, 1000],
  sw: [0, 100, 250, 500, 750, 1000],
  ow: [100, 250, 500, 750, 1000],
  iw: [0, 1, 2, 3],
  sv: [1, 2, 3, 4, 5, 6],
  weird: [0, 500, 1000, 2000, 3000],
};

const UNSET_FIELD_LABELS: Readonly<Record<string, string>> = {
  seed: "common.randomUnset",
  output_compression: "common.noCompressionUnset",
};

const GPT_SIZE_CHOICES = ["auto", "1024x1024", "1536x1024", "1024x1536", "2048x1152", "1152x2048", "2560x1440", "3840x2160"] as const;
const WAN_SIZE_CHOICES = ["2K", "1K", "4K", "1024*1024", "2048*2048", "2560*1440", "1440*2560"] as const;
const QWEN_SIZE_CHOICES = ["1024*1024", "1536*1024", "1024*1536", "2048*1024", "1024*2048"] as const;

export function numericFieldChoices(field: SchemaFieldDefinition): number[] {
  if (field.control === "range" || field.control === "range-with-auto") return [];
  const inBounds = (value: number) => (field.minimum == null || value >= field.minimum) && (field.maximum == null || value <= field.maximum);
  const preset = NUMERIC_FIELD_PRESETS[field.key];
  if (preset) return preset.filter(inBounds);
  if (field.key === "n" && field.maximum != null) {
    return Array.from({ length: Math.min(field.maximum, 24) }, (_, index) => index + 1);
  }
  if (field.minimum != null && field.maximum != null && field.maximum > field.minimum) {
    const span = field.maximum - field.minimum;
    if (span <= 12 && (field.type === "integer" || (field.step ?? 1) === 1)) {
      return Array.from({ length: Math.round(span) + 1 }, (_, index) => field.minimum! + index);
    }
    const middle = Math.round((field.minimum + field.maximum) / 2);
    return [...new Set([field.minimum, middle, field.maximum])];
  }
  return [];
}

/** Free-typed studio parameters become fixed dropdowns; enum, boolean, slider and JSON fields keep their controls. */
export function fixedFieldChoices(field: SchemaFieldDefinition): { values: string[]; numeric: boolean; unsetLabel?: string } | null {
  if (field.options.length > 0 || field.type === "boolean") return null;
  if (field.type === "number" || field.type === "integer") {
    const choices = numericFieldChoices(field);
    return choices.length ? { values: choices.map(String), numeric: true, unsetLabel: UNSET_FIELD_LABELS[field.key] ? translateKey(UNSET_FIELD_LABELS[field.key]!) : undefined } : null;
  }
  if (field.key === "n" && field.maximum != null && field.control !== "range" && field.control !== "range-with-auto") {
    // xAI-style nullable integers surface as strings; the bounds still define the ladder.
    return { values: Array.from({ length: Math.min(field.maximum, 24) }, (_, index) => String(index + 1)), numeric: true };
  }
  if (field.key === "size" && field.type === "string") {
    const pattern = typeof field.schema.pattern === "string" ? field.schema.pattern : "";
    if (pattern.includes("auto")) return { values: [...GPT_SIZE_CHOICES], numeric: false };
    if (pattern.includes("1K")) return { values: [...WAN_SIZE_CHOICES], numeric: false };
    if (pattern.includes("*")) return { values: [...QWEN_SIZE_CHOICES], numeric: false, unsetLabel: translateKey("common.followInputUnset") };
    const examples = Array.isArray(field.schema.examples)
      ? field.schema.examples.filter((item): item is string => typeof item === "string" && item.length > 0)
      : [];
    if (examples.length > 0) return { values: [...new Set(examples)], numeric: false };
  }
  return null;
}

export function SchemaField({
  field,
  model,
  value,
  onChange,
  onValidityChange,
  appearance,
  emptyNumberBehavior = "zero",
  textareaClassName,
  multiline: multilineOverride,
  placeholder,
  referenceContainer,
  textActionsContainer,
}: Readonly<{ textActionsContainer?: HTMLElement | null; referenceContainer?: HTMLElement | null; placeholder?: string; multiline?: boolean; emptyNumberBehavior?: "zero" | "omit"; textareaClassName?: string; field: SchemaFieldDefinition; model?: string; value: JsonValue | undefined; onChange(value: JsonValue | undefined): void; onValidityChange?(valid: boolean): void; appearance?: "playground" | "composer" }>) {
  const id = `field-${field.key}`;
  const playground = appearance === "playground";
  const defaultHint = fieldDefaultHint(field);
  const label = (
    <span className={appearance === "composer" ? "sr-only" : "field-label"}>
      {field.title}{field.required ? <b> *</b> : null}
    </span>
  );
  const hint = playground || !(field.description || defaultHint)
    ? null
    : <small>{field.description}{field.description && defaultHint ? " · " : null}{defaultHint ? <span className="field-default">{translateKey("common.default")} {defaultHint}</span> : null}</small>;
  if (field.control === "content-parts") {
    return <ContentPartsField compact={playground} composer={appearance === "composer"} referenceContainer={referenceContainer} textActionsContainer={textActionsContainer} placeholder={placeholder} schema={field.schema} value={value} onChange={onChange} onValidityChange={onValidityChange} />;
  }
  if (field.control === "resource" && field.resource) {
    return <ResourceSchemaField field={field} model={field.resourceModel ?? model} value={value} onChange={onChange} />;
  }
  if (field.options.length > 0) {
    if (playground) {
      const effective = value !== undefined ? value : field.schema.default;
      const canUnset = !field.required && field.schema.default === undefined && !field.options.includes("");
      const options = field.options.map((option) => ({
        value: String(option),
        label: localizedOptionLabel(option, field.optionLabels[String(option)]),
      }));
      if (canUnset) options.unshift({ value: "", label: translateKey("common.unspecified") });
      // Keep segmented only for short enums; denser option sets use a dropdown.
      const useSegmented = !canUnset && field.control !== "select" && field.options.length <= 4;
      if (useSegmented && effective !== undefined && effective !== null) {
        return (
          <div className="field field-inline">
            {label}
            <Segmented
              ariaLabel={field.title}
              compact
              onChange={(next) => onChange(coerceOption(next, field.options))}
              options={options}
              value={String(effective)}
            />
          </div>
        );
      }
      return (
        <label className="field field-inline">
          {label}
          <AdminSelectMenu
            ariaLabel={field.title}
            className="h-9 min-h-9 w-auto min-w-[8rem] text-xs"
            onChange={(next) => onChange(canUnset && next === "" ? undefined : coerceOption(next, field.options))}
            options={options}
            placeholder={translateKey("common.selectPlaceholder")}
            value={String(effective ?? "")}
          />
        </label>
      );
    }
    if (field.options.length <= 8) {
      return (
        <fieldset className="field" id={id}>
          {label}
          <div className="choice-grid">{field.options.map((option) => {
            const effective = value === undefined ? field.schema.default : value;
            const selected = String(effective === undefined ? "" : effective) === String(option);
            return <button aria-pressed={selected} className={selected ? "active" : ""} key={String(option)} onClick={() => onChange(option)} type="button">{localizedOptionLabel(option, field.optionLabels[String(option)])}</button>;
          })}</div>
          {hint}
        </fieldset>
      );
    }
    return (
      <label className="field" htmlFor={id}>
        {label}
        <SelectField
          id={id}
          onValueChange={(next) => onChange(coerceOption(next, field.options))}
          options={field.options.map((option) => ({
            value: String(option),
            label: localizedOptionLabel(option, field.optionLabels[String(option)]),
          }))}
          placeholder={translateKey("common.selectPlaceholder")}
          value={String(value === undefined ? "" : value)}
        />
        {hint}
      </label>
    );
  }
  if (field.type === "boolean") {
    const switchLabel = (
      <span className="field-label inline-flex items-center gap-1">
        <span>{field.title}{field.required ? <b> *</b> : null}</span>
        {playground && field.description ? <Info aria-hidden="true" className="size-3.5 text-zinc-400" /> : null}
      </span>
    );
    return (
      <label className={playground ? "switch-field switch-field-inline" : "switch-field"} htmlFor={id}>
        <span className="switch-field-copy">
          {playground && field.description
            ? <TermExplanation align="start" explanation={field.description} side="right">{switchLabel}</TermExplanation>
            : switchLabel}
          {playground ? null : hint}
        </span>
        {playground ? (
          <Switch
            aria-label={field.title}
            checked={Boolean(value ?? field.schema.default)}
            id={id}
            onCheckedChange={onChange}
          />
        ) : (
          <Input id={id} type="checkbox" checked={Boolean(value ?? field.schema.default)} onChange={(event) => onChange(event.target.checked)} />
        )}
      </label>
    );
  }
  if (playground) {
    const fixed = fixedFieldChoices(field);
    if (fixed) {
      const effective = value !== undefined ? value : field.schema.default;
      const current = effective === undefined || effective === null ? "" : String(effective);
      const values = current && !fixed.values.includes(current) ? [current, ...fixed.values] : fixed.values;
      return (
        <label className="field field-inline">
          {label}
          <AdminSelectMenu
            ariaLabel={field.title}
            className="h-9 min-h-9 w-auto min-w-[8rem] text-xs"
            onChange={(next) => {
              if (next === "") {
                onChange(undefined);
                return;
              }
              onChange(fixed.numeric ? Number(next) : next);
            }}
            options={[
              ...(fixed.unsetLabel ? [{ value: "", label: fixed.unsetLabel }] : []),
              ...values.map((option) => ({ value: option, label: `${option}${field.unit ? ` ${field.unit}` : ""}` })),
            ]}
            placeholder={translateKey("common.selectPlaceholder")}
            value={current}
          />
        </label>
      );
    }
  }
  if (field.type === "number" || field.type === "integer") {
    if (
      field.minimum != null
      && field.maximum != null
      && (field.control === "range" || field.control === "range-with-auto")
    ) {
      const effective = value ?? field.schema.default;
      const automatic = field.control === "range-with-auto" && effective === field.autoValue;
      const current = typeof effective === "number" && !automatic ? effective : field.minimum;
      const fillPercent = field.maximum === field.minimum ? 0 : ((current - field.minimum) / (field.maximum - field.minimum)) * 100;
      return (
        <label className="field field-inline" htmlFor={id}>
          {label}
          <div className="field-control-row">
            <Input className="slider" disabled={automatic} id={id} type="range" min={field.minimum} max={field.maximum} step={field.step ?? (field.type === "integer" ? 1 : 0.1)} style={{ "--fill": `${fillPercent}%` } as CSSProperties} value={current} onChange={(event) => onChange(Number(event.target.value))} />
            <output className={automatic ? "opacity-40" : undefined}>{`${current}${field.unit ? ` ${field.unit}` : ""}`}</output>
            {field.control === "range-with-auto" ? <button aria-pressed={automatic} className={`auto-toggle ${automatic ? "active" : ""}`} onClick={(event) => { event.preventDefault(); onChange(automatic ? field.minimum! : field.autoValue ?? field.minimum!); }} type="button">{translateKey("common.automatic")}</button> : null}
          </div>
        </label>
      );
    }
    return (
      <label className="field" htmlFor={id}>
        {label}
        <Input
          id={id}
          type="number"
          min={field.minimum}
          max={field.maximum}
          step={field.step ?? (field.type === "integer" ? 1 : 0.1)}
          value={typeof value === "number" ? value : ""}
          onChange={(event) => onChange(emptyNumberBehavior === "omit" && event.target.value === "" ? undefined : Number(event.target.value))}
        />
        {hint}
      </label>
    );
  }
  if (field.control !== "json" && (field.control === "media" || /^(images|videos|audios)$/i.test(field.key))) {
    const items = mediaFieldItems(value, field.roles[0], field.accept[0] ?? "url");
    const canChooseRole = field.roles.length > 1;
    const updateItem = (index: number, next: MediaFieldItem) => onChange(
      items.map((item, itemIndex) => itemIndex === index ? mediaFieldValue(next) : mediaFieldValue(item)),
    );
    return (
      <fieldset className="field media-field" id={id}>
        {label}
        <div className="media-field-items">{items.map((item, index) => <div className={`media-field-row${canChooseRole ? " has-role" : ""}${field.accept.length > 1 ? " has-source" : ""}${field.requiresReferenceName ? " has-reference-name" : ""}`} key={index}>
          {canChooseRole ? <SelectField aria-label={`${field.title}用途 ${index + 1}`} onValueChange={(next) => updateItem(index, { ...item, role: next })} options={field.roles.map((role) => ({ value: role, label: mediaRoleLabel(role) }))} size="sm" value={item.role ?? field.roles[0]} /> : null}
          {field.accept.length > 1 ? <SelectField aria-label={`${field.title}来源 ${index + 1}`} onValueChange={(next) => updateItem(index, { ...item, type: next })} options={field.accept.map((type) => ({ value: type, label: mediaSourceLabel(type) }))} size="sm" value={item.type} /> : null}
          <Input aria-label={`${field.title} ${mediaSourceLabel(item.type)} ${index + 1}`} value={item.value} onChange={(event) => updateItem(index, { ...item, value: event.target.value })} placeholder={mediaPlaceholder(item.type)} />
          {field.requiresReferenceName ? <Input aria-label={`${field.title}参考名称 ${index + 1}`} value={item.referenceName ?? ""} onChange={(event) => updateItem(index, { ...item, referenceName: event.target.value })} placeholder="参考名称" /> : null}
          <button aria-label={`移除${field.title} ${index + 1}`} onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index).map(mediaFieldValue))} type="button">×</button>
        </div>)}</div>
        {field.maxItems == null || items.length < field.maxItems ? <button className="add-media-button" onClick={() => onChange([...items.map(mediaFieldValue), mediaFieldValue({ type: field.accept[0] ?? "url", value: "", ...(field.roles[0] ? { role: field.roles[0] } : {}), ...(field.requiresReferenceName ? { referenceName: "" } : {}) })])} type="button">＋ 添加{field.title}</button> : null}
        {hint ?? <small>支持 {field.accept.map(mediaSourceLabel).join("、")} 输入；文件上传后续通过资源库提供。</small>}
      </fieldset>
    );
  }
  if (field.type === "object" || field.type === "array" || field.control === "json" || field.control === "json-or-text") {
    return <JsonSchemaField field={field} value={value} onChange={onChange} onValidityChange={onValidityChange} textareaClassName={textareaClassName} />;
  }
  const multiline = multilineOverride ?? /prompt|text|script|description/i.test(field.key);
  return (
    <label className="field" htmlFor={id}>
      {label}
      {multiline ? (
        <Textarea className={textareaClassName} variant={appearance === "composer" ? "composer" : "default"} placeholder={placeholder} id={id} value={typeof value === "string" ? value : ""} onChange={(event) => onChange(event.target.value)} rows={appearance === "composer" ? 3 : 5} />
      ) : (
        <Input id={id} value={typeof value === "string" ? value : value == null ? "" : JSON.stringify(value)} onChange={(event) => onChange(event.target.value)} />
      )}
      {hint}
    </label>
  );
}

function ResourceSchemaField({
  field,
  model,
  value,
  onChange,
}: Readonly<{
  field: SchemaFieldDefinition;
  model?: string;
  value: JsonValue | undefined;
  onChange(value: JsonValue): void;
}>) {
  const accountKey = useAccountQueryKey();
  const id = `field-${field.key}`;
  const listId = `${id}-choices`;
  const query = useQuery({
    ...catalogQueryPolicy,
    queryKey: accountKey("model-resource", model, field.resource),
    ...sensitiveQueryPolicy,
    queryFn: ({ signal }) => dataApi.modelResources(model!, field.resource!, signal),
    enabled: Boolean(model && field.resource),
    retry: false,
  });
  const [manual, setManual] = useState(false);
  const current = typeof value === "string" ? value : "";
  const options = query.data?.data ?? [];
  const selected = options.find((option) => option.id === current);
  const manualMode = manual || Boolean(current && options.length > 0 && !selected);
  const message = query.data?.message
    ?? (query.error instanceof ApiError && query.error.status === 401
      ? translateKey("workspace.needSignIn")
      : query.error instanceof Error ? query.error.message : null);
  return (
    <label className="field" htmlFor={id}>
      <span>{field.title}{field.required ? <b> *</b> : null}</span>
      {options.length > 0 && !manualMode ? (
        <div className="resource-choice-row">
          <SelectField
            id={id}
            onValueChange={onChange}
            options={options.map((option) => ({
              value: option.id,
              label: option.description ? `${option.label} · ${option.description}` : option.label,
            }))}
            placeholder="选择音色"
            value={current}
          />
          <Button variant="outline" size="sm" onClick={() => setManual(true)} type="button">自定义 ID</Button>
        </div>
      ) : (
        <div className="resource-choice-row">
          <Input
            autoComplete="off"
            id={id}
            list={options.length > 0 ? listId : undefined}
            placeholder={query.isLoading ? "正在加载选项…" : "输入音色 ID"}
            value={current}
            onChange={(event) => onChange(event.target.value)}
          />
          {options.length > 0 ? <Button variant="outline" size="sm" onClick={() => setManual(false)} type="button">{translateKey("common.choose")}</Button> : null}
          {options.length > 0 ? (
            <datalist id={listId}>
              {options.map((option) => (
                <option key={option.id} label={[option.label, option.description].filter(Boolean).join(" · ")} value={option.id} />
              ))}
            </datalist>
          ) : null}
        </div>
      )}
      {selected?.previewUrl ? (
        <small className="resource-choice-meta">
          <span>{field.description ?? "运行模型前可以试听所选音色。"}</span>
          <a href={selected.previewUrl} rel="noreferrer" target="_blank">试听 ↗</a>
        </small>
      ) : message ? <small>{message}</small> : field.description ? <small>{field.description}</small> : null}
    </label>
  );
}

export function initialModelInput(schema?: ModelSchema): JsonObject {
  const example = schema?.examples[0];
  if (isJsonObject(example)) return structuredClone(example);
  const defaults = schema ? { ...schema.defaults, ...modelInputDefaults(schema.inputSchema) } : undefined;
  if (defaults && Object.keys(defaults).length > 0) return defaults;
  const result: JsonObject = {};
  for (const field of schemaFields(schema)) {
    const definition = propertyDefinition(schema, field.key);
    if (definition && "default" in definition) result[field.key] = definition.default as JsonValue;
  }
  return result;
}

export function formatDate(value?: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function formatDuration(value?: number | null): string {
  if (value == null) return "—";
  const rounded = Number(value.toFixed(2));
  return rounded < 1_000 ? `${rounded} ms` : `${(value / 1_000).toFixed(2)} s`;
}

const currencySymbols: Readonly<Record<string, string>> = { CNY: "¥", USD: "$" };
const MONEY_DISPLAY_DECIMALS = 2;

export function formatMoneyNumber(amount: string | number, _currency?: string): string {
  if (typeof amount === "string" && /^-?\d+(\.\d{1,8})?$/.test(amount)) return roundDecimalString(amount, MONEY_DISPLAY_DECIMALS);
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return String(amount);
  return value.toLocaleString(getLocale(), { minimumFractionDigits: MONEY_DISPLAY_DECIMALS, maximumFractionDigits: MONEY_DISPLAY_DECIMALS });
}

function roundDecimalString(amount: string, digits: number): string {
  const negative = amount.startsWith("-");
  const unsigned = negative ? amount.slice(1) : amount;
  const [wholePart = "0", fractionPart = ""] = unsigned.split(".");
  const padded = fractionPart.padEnd(digits + 1, "0");
  let fraction = BigInt(padded.slice(0, digits) || "0");
  let whole = BigInt(wholePart);
  if ((padded[digits] ?? "0") >= "5") {
    fraction += 1n;
    const cap = 10n ** BigInt(digits);
    if (fraction >= cap) {
      whole += 1n;
      fraction = 0n;
    }
  }
  const sign = negative && (whole > 0n || fraction > 0n) ? "-" : "";
  return `${sign}${whole.toLocaleString(getLocale())}.${fraction.toString().padStart(digits, "0")}`;
}

export function formatMoney(currency: string, amount: string | number): string {
  return `${currencySymbols[currency] ?? `${currency} `}${formatMoneyNumber(amount, currency)}`;
}

export function formatListedPrice(currency: string, amount: string): string {
  return `${currency} ${formatMoneyNumber(amount, currency)}`;
}

export function shortId(value: string): string {
  return value.length <= 16 ? value : `${value.slice(0, 8)}…${value.slice(-5)}`;
}

export function safeUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function safeMediaUrl(value?: string | null, kind?: string): string | undefined {
  if (!value) return undefined;
  const expectedType = kind === "image" || kind === "video" || kind === "audio" ? kind : undefined;
  if (expectedType && value.startsWith(`data:${expectedType}/`) && /^data:[a-z]+\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+$/i.test(value)) {
    return value;
  }
  return safeUrl(value);
}


export function schemaFields(schema?: ModelSchema): SchemaFieldDefinition[] {
  const properties = schema?.inputSchema.properties;
  if (!isJsonObject(properties)) return [];
  const required = new Set(Array.isArray(schema?.inputSchema.required) ? schema.inputSchema.required.filter((item): item is string => typeof item === "string") : []);
  const primary = new Set(Array.isArray(schema?.inputSchema.anyOf) ? schema.inputSchema.anyOf.flatMap((branch) => isJsonObject(branch) && Array.isArray(branch.required) ? branch.required.filter((item): item is string => typeof item === "string") : []) : []);
  const uiFields = isJsonObject(schema?.uiSchema.fields) ? schema.uiSchema.fields : {};
  const order = schema ? orderedSchemaProperties(schema.inputSchema).map(([key]) => key) : [];
  const keys = [...new Set([...order, ...Object.keys(properties)])];
  return keys.flatMap((key) => {
    const raw = properties[key];
    if (!isJsonObject(raw)) return [];
    const ui = { ...(isJsonObject(uiFields[key]) ? uiFields[key] : {}), ...(isJsonObject(raw["x-ui"]) ? raw["x-ui"] : {}) };
    const optionLabels = isJsonObject(ui.optionLabels) ? Object.fromEntries(Object.entries(ui.optionLabels).filter((entry): entry is [string, string] => typeof entry[1] === "string")) : {};
    const exampleOptions = Array.isArray(raw.examples)
      ? raw.examples.filter((item): item is string => typeof item === "string" && item.length > 0)
      : [];
    const options = Array.isArray(raw.enum)
      ? raw.enum
      : [...new Set([...Object.keys(optionLabels), ...exampleOptions])];
    const numericBranch = Array.isArray(raw.anyOf) ? raw.anyOf.find((branch) => isJsonObject(branch) && (branch.type === "number" || branch.type === "integer")) : undefined;
    const numeric = isJsonObject(numericBranch) ? numericBranch : {};
    const rawItems = isJsonObject(raw.items) ? raw.items : {};
    const objectItem = rawItems.type === "object"
      ? rawItems
      : Array.isArray(rawItems.oneOf)
        ? rawItems.oneOf.find((branch) => isJsonObject(branch) && branch.type === "object")
        : undefined;
    const itemProperties = isJsonObject(objectItem) && isJsonObject(objectItem.properties)
      ? objectItem.properties
      : {};
    const itemRequired = isJsonObject(objectItem) && Array.isArray(objectItem.required)
      ? objectItem.required
      : [];
    const unionTypes = Array.isArray(raw.type) ? raw.type.filter((item): item is string => typeof item === "string") : [];
    const inferredType = typeof raw.type === "string"
      ? raw.type
      : unionTypes.includes("integer")
        ? "integer"
        : unionTypes.includes("number")
          ? "number"
        : typeof numeric.type === "string"
          ? numeric.type
          : /^(images|videos|audios|files)$/i.test(key)
            ? "array"
            : "string";
    return [{
      schema: raw,
      key,
      title: localizedFieldTitle(key, typeof raw.title === "string" ? raw.title : undefined),
      ...(typeof raw.description === "string" ? { description: localizedFieldDescription(key, raw.description) } : {}),
      type: inferredType,
      required: required.has(key),
      primary: primary.has(key) || ui.primary === true,
      ...(typeof ui.control === "string" ? { control: ui.control } : {}),
      ...(typeof ui.unit === "string" ? { unit: ui.unit } : {}),
      ...(typeof ui.resource === "string" ? { resource: ui.resource } : {}),
      ...(typeof ui.resourceModel === "string" ? { resourceModel: ui.resourceModel } : {}),
      options,
      optionLabels,
      ...(typeof raw.minimum === "number" ? { minimum: raw.minimum } : typeof numeric.minimum === "number" ? { minimum: numeric.minimum } : typeof ui.minimum === "number" ? { minimum: ui.minimum } : {}),
      ...(typeof raw.maximum === "number" ? { maximum: raw.maximum } : typeof numeric.maximum === "number" ? { maximum: numeric.maximum } : typeof ui.maximum === "number" ? { maximum: ui.maximum } : {}),
      ...(typeof raw.multipleOf === "number" ? { step: raw.multipleOf } : typeof numeric.multipleOf === "number" ? { step: numeric.multipleOf } : typeof ui.step === "number" ? { step: ui.step } : {}),
      ...(ui.autoValue !== undefined ? { autoValue: ui.autoValue } : {}),
      roles: Array.isArray(ui.roles) ? ui.roles.filter((item): item is string => typeof item === "string") : [],
      accept: Array.isArray(ui.accept) ? ui.accept.filter((item): item is string => typeof item === "string") : [],
      ...(typeof raw.maxItems === "number" ? { maxItems: raw.maxItems } : {}),
      requiresReferenceName: "referenceName" in itemProperties && itemRequired.includes("referenceName"),
    }];
  });
}

type MediaFieldItem = Readonly<{ type: string; value: string; role?: string; referenceName?: string }>;

function mediaFieldItems(
  value: JsonValue | undefined,
  defaultRole: string | undefined,
  defaultType: string,
): MediaFieldItem[] {
  const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  return values.flatMap((item) => {
    if (typeof item === "string") return [{ type: defaultType, value: item, ...(defaultRole ? { role: defaultRole } : {}) }];
    if (!isJsonObject(item)) return [];
    const representation = typeof item.url === "string"
      ? { type: "url", value: item.url }
      : typeof item.base64 === "string"
        ? { type: "base64", value: item.base64 }
        : typeof item.assetId === "string"
          ? { type: "asset_id", value: item.assetId }
          : typeof item.fileId === "string"
          ? { type: "file_id", value: item.fileId }
          : typeof item.voiceId === "string"
            ? { type: "voice_id", value: item.voiceId }
          : null;
    if (!representation) return [];
    return [{
      ...representation,
      ...(typeof item.role === "string" ? { role: item.role } : defaultRole ? { role: defaultRole } : {}),
      ...(typeof item.referenceName === "string" ? { referenceName: item.referenceName } : {}),
    }];
  });
}

function mediaFieldValue(item: MediaFieldItem): JsonObject {
  const value: JsonObject = item.type === "base64"
    ? { base64: item.value }
    : item.type === "asset_id"
      ? { assetId: item.value }
      : item.type === "file_id"
      ? { fileId: item.value }
      : item.type === "voice_id"
        ? { voiceId: item.value }
      : { url: item.value };
  if (item.role) value.role = item.role;
  if (item.referenceName !== undefined) value.referenceName = item.referenceName;
  return value;
}

function mediaSourceLabel(value: string): string {
  if (value === "base64") return "Base64";
  if (value === "asset_id") return "资源 ID";
  if (value === "file_id") return "文件 ID";
  if (value === "voice_id") return "预设音色";
  return "URL";
}

function mediaPlaceholder(value: string): string {
  if (value === "base64") return "data:image/png;base64,…";
  if (value === "asset_id") return "asset_…";
  if (value === "file_id") return "file_…";
  if (value === "voice_id") return "eve";
  return "https://…";
}

function JsonSchemaField({
  field,
  value,
  onChange,
  onValidityChange,
  textareaClassName,
}: Readonly<{ field: SchemaFieldDefinition; value: JsonValue | undefined; onChange(value: JsonValue): void; onValidityChange?(valid: boolean): void; textareaClassName?: string }>) {
  const initial = value === undefined ? "" : typeof value === "string" ? value : JSON.stringify(value, null, 2);
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    onValidityChange?.(!error);
  }, [error, onValidityChange]);
  useEffect(() => () => onValidityChange?.(true), []);
  useEffect(() => {
    setDraft(value === undefined ? "" : typeof value === "string" ? value : JSON.stringify(value, null, 2));
    setError(null);
  }, [value]);
  function update(next: string) {
    setDraft(next);
    if (field.control === "json-or-text" && !next.trim().startsWith("{") && !next.trim().startsWith("[")) {
      setError(null);
      onChange(next);
      return;
    }
    try {
      const parsed = JSON.parse(next) as JsonValue;
      if (field.type === "array" && !Array.isArray(parsed)) throw new Error("请输入 JSON 数组");
      if (field.type === "object" && !isJsonObject(parsed)) throw new Error("请输入 JSON 对象");
      setError(null);
      onChange(parsed);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "JSON 格式不正确");
    }
  }
  return (
    <label className="field" htmlFor={`field-${field.key}`}>
      <span className="field-label">{field.title}{field.required ? <b> *</b> : null}</span>
      <Textarea className={`json-editor min-h-32 ${textareaClassName ?? ""}`} id={`field-${field.key}`} value={draft} onChange={(event) => update(event.target.value)} spellCheck={false} />
      {field.description ? <small>{field.description}</small> : null}
      {error ? <small className="field-error">{error}</small> : null}
    </label>
  );
}

function propertyDefinition(schema: ModelSchema | undefined, key: string): JsonObject | undefined {
  const properties = schema?.inputSchema.properties;
  if (!isJsonObject(properties)) return undefined;
  const definition = properties[key];
  return isJsonObject(definition) ? definition : undefined;
}

function humanize(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function localizedFieldTitle(key: string, fallback?: string): string {
  if (fallback === translateKey("fields.resolution", { lng: "zh-CN" })) return translateKey("fields.resolution");
  // A title in the active language may carry meaning the generic field name
  // cannot express (for example, platform model ID versus upstream model ID).
  if (fallback && (getLocale() === "zh-CN" ? containsChinese(fallback) : !containsChinese(fallback))) return fallback;
  if (hasTranslation(`fields.${key}`)) return translateKey(`fields.${key}`);
  if (fallback && !containsChinese(fallback)) return fallback;
  return fallback && getLocale() === "zh-CN" ? fallback : humanize(key);
}

function localizedFieldDescription(key: string, description: string): string {
  const localized = localizedParameterDescription(description);
  if (localized !== description) return localized;
  // Keep authored Chinese copy in zh-CN and authored English copy in English;
  // unknown cross-locale descriptions fall back without discarding constraints.
  if (getLocale() === "zh-CN") return description;
  if (description && !containsChinese(description)) return description;
  return translateKey("common.setField", { title: localizedFieldTitle(key) });
}

export function localizedOptionLabel(value: JsonValue, label?: string): string {
  const raw = String(value);
  const key = optionTranslationKey(raw);
  if (hasTranslation(`options.${key}`)) return translateKey(`options.${key}`);
  const lower = optionTranslationKey(raw.toLowerCase());
  if (hasTranslation(`options.${lower}`)) return translateKey(`options.${lower}`);
  if (label && !containsChinese(label)) return label;
  if (label && getLocale() === "zh-CN") return label;
  return raw;
}

function optionTranslationKey(value: string): string {
  if (/^\d+:\d+$/.test(value)) return `ratio_${value.replace(":", "_")}`;
  return value;
}

function localizedCatalogText(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  if (getLocale() === "zh-CN") return containsChinese(value) ? value : fallback;
  return containsChinese(value) ? fallback : value;
}

function containsChinese(value: string): boolean {
  return /[㐀-鿿]/.test(value);
}

function accessLabel(value: string): string {
  if (value === "task_adapter") return "任务 API";
  if (value === "passthrough") return "原生 API";
  if (value === "bridge") return "兼容 API";
  return value;
}

function operationLabel(value: string): string {
  const exact: Readonly<Record<string, string>> = {
    language: "文本",
    image: "图片",
    video: "视频",
    audio: "音频",
    speech: "语音",
    world: "世界模型",
    embedding: "向量",
    reranker: "重排",
    multimodal: "多模态",
    text_to_image: "文生图",
    image_to_image: "图生图",
    text_to_video: "文生视频",
    image_to_video: "图生视频",
    text_to_speech: "文字转语音",
    speech_to_text: "语音转文字",
  };
  if (exact[value.toLowerCase()]) return exact[value.toLowerCase()];
  const [subject, action] = value.split(".");
  const subjects: Readonly<Record<string, string>> = {
    image: "图片",
    video: "视频",
    audio: "音频",
    speech: "语音",
    text: "文本",
    world: "世界",
    embedding: "向量",
  };
  if (action === "generate") return `${subjects[subject ?? ""] ?? subject}生成`;
  if (subject === "embedding" && action === "create") return "向量生成";
  return value.replaceAll(".", " ").replaceAll("_", " ");
}


function mediaRoleLabel(value: string): string {
  if (hasTranslation(`roles.${value}`)) return translateKey(`roles.${value}`);
  return humanize(value);
}

function hasEditorValue(value: JsonValue | undefined): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function isMediaField(field: SchemaFieldDefinition): boolean {
  return field.control === "media" || /^(images|videos|audios|files)$/i.test(field.key);
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function coerceOption(value: string, options: JsonValue[]): JsonValue {
  return options.find((option) => String(option) === value) ?? value;
}
